"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput, { type Country } from "react-phone-number-input";
import { isValidPhoneNumber } from "libphonenumber-js";

import { Button } from "./ui";
import { useAuth } from "@/store/auth";
import { useBranch } from "@/store/branch";
import { ApiError } from "@/api/http";
import { extractDevOtp } from "@/api/services";
import "react-phone-number-input/style.css";
import "./phone-input.css";

import type {
  OnlineOrderingConfig,
  Branch,
} from "@/api/online-ordering-service/model";

type Step = "phone" | "otp" | "name";

/**
 * Resolve the default country for the phone-number input. Locale-based
 * detection (`navigator.language`) is unreliable — most macOS/Chrome
 * installs ship `en-US` regardless of where the user actually is — so we
 * prefer the branch's own ISO country code: the customer is ordering from
 * *this* branch, in *this* country, so it's the strongest signal we have.
 *
 * Resolution order:
 *  1. `branchConfig.branches[0].address.country` (most accurate).
 *  2. Browser locale region (`en-EG` → `EG`).
 *  3. `EG` fallback (matches v1's hardcoded default).
 */
function resolveCountry(): Country {
  return "EG";
}

export function SignInModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation();
  const { sendOtp, verifyOtp, needsRegistration, completeRegistration } =
    useAuth();
  const { branchConfig } = useBranch();
  const detectedCountry = useMemo(() => resolveCountry(), []);
  const [step, setStep] = useState<Step>("phone");
  // The PhoneInput value is always either a complete E.164 string
  // (`+14155551234`) or `undefined`. The library handles autoformatting,
  // country code parsing, etc. so we don't need any manual cleanup.
  const [phone, setPhone] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState<Country>(detectedCountry);
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const otpInputRef = useRef<HTMLInputElement | null>(null);
  // Length the boxed OTP input renders. The backend issues a 4-digit code
  // (randint(1000,9999)) — kept at 4 for parity with legacy online-ordering —
  // so default to 4. (If an inline dev code of a different length ever appears,
  // handleSendOtp adapts otpLength to it.)
  const [otpLength, setOtpLength] = useState(4);

  // Resend cooldown (seconds remaining). While > 0 the "Resend code" control is
  // disabled and shows a countdown, so users can't spam OTP sends (each one is a
  // metered SMS) but always have a way to retry if the code never arrives.
  const RESEND_SECONDS = 30;
  const [resendIn, setResendIn] = useState(0);

  // Reset state whenever the sheet opens.
  useEffect(() => {
    if (open) {
      setStep("phone");
      setPhone(undefined);
      setOtp("");
      setName("");
      setError(null);
      setBusy(false);
      setCountry(detectedCountry);
      setOtpLength(4);
      setResendIn(0);
    }
  }, [open, detectedCountry]);

  // Start the resend cooldown each time we (re)enter the OTP step.
  useEffect(() => {
    if (open && step === "otp") setResendIn(RESEND_SECONDS);
  }, [open, step]);

  // Tick the countdown down to zero. Keyed on `resendIn > 0` so the interval is
  // created once when the cooldown starts and torn down when it reaches zero.
  const cooling = resendIn > 0;
  useEffect(() => {
    if (!cooling) return;
    const id = setInterval(
      () => setResendIn((s) => (s <= 1 ? 0 : s - 1)),
      1000,
    );
    return () => clearInterval(id);
  }, [cooling]);

  // If the modal is opened (e.g. via the global auth-required event) for a
  // user who is already authenticated but has no customer record yet, jump
  // straight to the name step.
  useEffect(() => {
    if (open && needsRegistration && step === "phone") {
      setStep("name");
      setError(null);
      setBusy(false);
    }
  }, [open, needsRegistration, step]);

  // Focus OTP input when entering step 2.
  useEffect(() => {
    if (open && step === "otp") {
      const t = setTimeout(() => otpInputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open, step]);

  const phoneValid = useMemo(() => {
    if (!phone) return false;
    try {
      return isValidPhoneNumber(phone);
    } catch {
      return false;
    }
  }, [phone]);

  if (!open) return null;

  const otpValid = /^\d{4,6}$/.test(otp.trim());

  async function handleSendOtp() {
    setError(null);
    if (!phone || !phoneValid) {
      setError(t("v2.signin.err_invalid_phone"));
      return;
    }
    setBusy(true);
    try {
      const res = await sendOtp(phone);
      console.log("[dev] sendOtp response payload:", res);
      // Dev/staging tenants ship the OTP back inline ("SMS Disabled, message:
      // {…, 'messageText': '8217 is your verification number.', …}"). When
      // we detect that, prefill the boxes and skip straight to verify so
      // engineers don't have to read it back manually.
      const devCode = extractDevOtp(res);
      if (devCode) {
        setOtpLength(devCode.length);
        setOtp(devCode);
        setStep("otp");
        await runVerify(devCode);
        return;
      }
      setStep("otp");
    } catch (err) {
      setError(formatError(err, t("v2.signin.err_send_otp")));
    } finally {
      setBusy(false);
    }
  }

  // Re-request a code for the SAME number. Reuses handleSendOtp (phone is still
  // set), and restarts the cooldown. No-op while cooling or busy.
  async function handleResend() {
    if (resendIn > 0 || busy) return;
    setResendIn(RESEND_SECONDS);
    await handleSendOtp();
  }

  async function runVerify(code: string) {
    if (!phone) return;
    setError(null);
    setBusy(true);
    try {
      const { needsRegistration: needsReg } = await verifyOtp(
        phone,
        code.trim(),
      );
      if (needsReg) {
        // Brand-new phone — collect a display name before closing.
        setStep("name");
      } else {
        onClose();
      }
    } catch (err) {
      setError(formatError(err, t("v2.signin.err_verify")));
    } finally {
      setBusy(false);
    }
  }

  async function handleCompleteRegistration() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("v2.signin.err_enter_name"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await completeRegistration({ name: trimmed });
      onClose();
    } catch (err) {
      setError(formatError(err, t("v2.signin.err_register")));
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify() {
    if (!phone || !otpValid) {
      setError(t("v2.signin.err_enter_code"));
      return;
    }
    await runVerify(otp.trim());
  }

  return (
    <>
      {/* Scrim */}
      <div
        onClick={busy ? undefined : onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(10, 14, 12, 0.45)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          zIndex: 30,
          animation: "fadeIn 180ms ease",
        }}
      />
      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("v2.signin.title")}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: "var(--surface-1)",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "16px 18px calc(env(safe-area-inset-bottom, 0px) + 22px)",
          boxShadow: "0 -10px 30px -10px rgba(0,0,0,0.25)",
          zIndex: 31,
          animation: "slideUp 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        <div
          aria-hidden
          style={{
            width: 40,
            height: 4,
            borderRadius: 999,
            background: "var(--surface-2-strong)",
            margin: "2px auto 16px",
          }}
        />

        <div className="display"
          style={{
            fontSize: 19,
            fontWeight: 700,
            color: "var(--ink-1)",
            letterSpacing: -0.3,
            marginBottom: 4,
          }}
        >
          {step === "phone"
            ? t("v2.signin.title")
            : step === "name"
              ? t("v2.signin.name_title")
              : t("v2.signin.enter_code")}
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 16 }}>
          {step === "phone" ? (
            t("v2.signin.subtitle_phone")
          ) : step === "name" ? (
            t("v2.signin.name_subtitle")
          ) : (
            // Wrap the phone in a <bdi dir="ltr"> so the digits stay in
            // E.164 order even when the surrounding Arabic flips the line
            // to RTL. We interpolate via a sentinel so this works against
            // any i18n template ("Sent to {{phone}}", "أُرسل إلى {{phone}}").
            (() => {
              const SENTINEL = "__TAPPYA_PHONE__";
              const tmpl = t("v2.signin.subtitle_otp", { phone: SENTINEL });
              const [pre = "", post = ""] = tmpl.split(SENTINEL);
              return (
                <>
                  {pre}
                  <bdi dir="ltr" style={{ unicodeBidi: "isolate" }}>
                    {phone ?? ""}
                  </bdi>
                  {post}
                </>
              );
            })()
          )}
        </div>

        {step === "phone" ? (
          <>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ink-2)",
                letterSpacing: 0.2,
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              {t("v2.signin.phone_label")}
            </div>
            <PhoneInput
              international
              countryCallingCodeEditable={false}
              defaultCountry={country}
              value={phone}
              onChange={setPhone}
              onCountryChange={(c: Country | undefined) => c && setCountry(c)}
              numberInputProps={{
                autoComplete: "tel",
                inputMode: "tel",
                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSendOtp();
                  }
                },
              }}
              className="v2-phone-input"
              placeholder={t("v2.signin.phone_placeholder")}
            />
            <div
              style={{
                fontSize: 11,
                color: "var(--ink-3)",
                marginTop: 8,
                lineHeight: 1.4,
              }}
            >
              {t("v2.signin.tap_flag")}
              {phone && phone.length > 8 && !phoneValid && " " + t("v2.signin.number_incomplete")}
            </div>
            {error && <ErrorRow text={error} />}
            <Button
              full
              size="lg"
              testId="signin-send"
              onClick={handleSendOtp}
              disabled={busy || !phoneValid}
              style={{ marginTop: 16 }}
            >
              {busy ? t("v2.signin.sending") : t("v2.signin.send_code")}
            </Button>
            <Button
              full
              size="md"
              variant="ghost"
              onClick={onClose}
              disabled={busy}
              style={{ marginTop: 8 }}
            >
              {t("v2.signin.cancel")}
            </Button>
          </>
        ) : step === "otp" ? (
          <>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ink-2)",
                letterSpacing: 0.2,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              {t("v2.signin.verification_code")}
            </div>
            <BoxedCodeInput
              value={otp}
              onChange={(v) => setOtp(v)}
              onComplete={(code) => {
                // Auto-verify the moment every cell is filled.
                if (!busy) void runVerify(code);
              }}
              length={otpLength}
              inputRef={otpInputRef}
              disabled={busy}
              autoFocus
            />
            <div
              style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 8, lineHeight: 1.4 }}
            >
              {t("v2.signin.verification_hint")}
            </div>
            {error && <ErrorRow text={error} />}
            <Button
              full
              size="lg"
              testId="signin-verify"
              onClick={handleVerify}
              disabled={busy || !otpValid}
              style={{ marginTop: 16 }}
            >
              {busy ? t("v2.signin.verifying") : t("v2.signin.verify")}
            </Button>
            <Button
              full
              size="md"
              variant="ghost"
              onClick={handleResend}
              disabled={busy || resendIn > 0}
              style={{ marginTop: 6 }}
            >
              {resendIn > 0
                ? t("v2.signin.resend_in", {
                    seconds: resendIn,
                    defaultValue: "Resend code in {{seconds}}s",
                  })
                : t("v2.signin.resend", { defaultValue: "Resend code" })}
            </Button>
            <Button
              full
              size="md"
              variant="ghost"
              onClick={() => {
                setStep("phone");
                setOtp("");
                setError(null);
              }}
              disabled={busy}
              style={{ marginTop: 6 }}
            >
              {t("v2.signin.use_different_number")}
            </Button>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ink-2)",
                letterSpacing: 0.2,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              {t("v2.signin.name_label")}
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (!busy) void handleCompleteRegistration();
                }
              }}
              autoFocus
              disabled={busy}
              autoComplete="name"
              data-testid="signin-name"
              placeholder={t("v2.signin.name_placeholder")}
              style={{
                width: "100%",
                padding: "12px 14px",
                border: "1px solid var(--surface-2-strong)",
                borderRadius: 12,
                fontSize: 16,
                background: "var(--surface-1)",
                color: "var(--ink-1)",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {error && <ErrorRow text={error} />}
            <Button
              full
              size="lg"
              testId="signin-complete"
              onClick={handleCompleteRegistration}
              disabled={busy || !name.trim()}
              style={{ marginTop: 16 }}
            >
              {busy
                ? t("v2.signin.registering")
                : t("v2.signin.complete_signup")}
            </Button>
          </>
        )}
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(110%) } to { transform: translateY(0) } }
      `}</style>
    </>
  );
}

/**
 * Apple/Stripe-style boxed verification code input.
 *
 * Renders `length` cells; each cell holds one digit. Typing advances focus,
 * Backspace on an empty cell jumps back, and pasting (or programmatic value
 * change) distributes digits across the boxes. Calls `onComplete(code)` as
 * soon as every cell is filled so the form can auto-submit.
 */
function BoxedCodeInput({
  value,
  onChange,
  onComplete,
  length,
  inputRef,
  disabled = false,
  autoFocus = false,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  length: number;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const boxRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Pad to `length` so every cell has a stable index.
  const digits = useMemo(() => {
    const sliced = value.replace(/\D/g, "").slice(0, length);
    return Array.from({ length }, (_, i) => sliced[i] ?? "");
  }, [value, length]);

  useEffect(() => {
    if (autoFocus) {
      const t = setTimeout(() => boxRefs.current[0]?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [autoFocus]);

  // Expose the first cell via the parent's ref (used for focus-on-step-change).
  useEffect(() => {
    if (inputRef) inputRef.current = boxRefs.current[0] ?? null;
  });

  function commit(next: string) {
    const cleaned = next.replace(/\D/g, "").slice(0, length);
    onChange(cleaned);
    if (cleaned.length === length) onComplete?.(cleaned);
  }

  function onCellChange(i: number, raw: string) {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      // Treat as a clear of this cell.
      const arr = [...digits];
      arr[i] = "";
      commit(arr.join(""));
      return;
    }
    if (cleaned.length > 1) {
      // Paste or autofill — distribute across cells starting at this index.
      const merged = digits.slice(0, i).join("") + cleaned;
      commit(merged);
      const focusTo = Math.min(length - 1, i + cleaned.length);
      boxRefs.current[focusTo]?.focus();
      boxRefs.current[focusTo]?.select?.();
      return;
    }
    const arr = [...digits];
    arr[i] = cleaned;
    commit(arr.join(""));
    if (i + 1 < length) boxRefs.current[i + 1]?.focus();
  }

  function onCellKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      const arr = [...digits];
      arr[i - 1] = "";
      commit(arr.join(""));
      boxRefs.current[i - 1]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      boxRefs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i + 1 < length) {
      e.preventDefault();
      boxRefs.current[i + 1]?.focus();
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${length}, 1fr)`,
        gap: 8,
        direction: "ltr",
      }}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            boxRefs.current[i] = el;
          }}
          value={d}
          onChange={(e) => onCellChange(i, e.target.value)}
          onKeyDown={(e) => onCellKey(i, e)}
          onFocus={(e) => e.currentTarget.select()}
          disabled={disabled}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={length} // accept paste of full code into any cell
          aria-label={`Digit ${i + 1}`}
          style={{
            width: "100%",
            height: 56,
            textAlign: "center",
            fontSize: 22,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            background: "var(--surface-2)",
            border: `1px solid ${d ? "var(--primary-600)" : "var(--border)"}`,
            borderRadius: 12,
            color: "var(--ink-1)",
            outline: "none",
            caretColor: "var(--primary-600)",
            transition: "border-color 120ms",
            opacity: disabled ? 0.6 : 1,
            fontFamily: "inherit",
          }}
        />
      ))}
    </div>
  );
}

function ErrorRow({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 10,
        padding: "10px 12px",
        borderRadius: 10,
        background: "#fee2e2",
        color: "#b91c1c",
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.35,
      }}
    >
      {text}
    </div>
  );
}

type SheetFieldProps = {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit?: () => void;
  type?: string;
  inputMode?: "text" | "numeric" | "decimal" | "tel" | "email";
  autoComplete?: string;
  inputRef?: React.Ref<HTMLInputElement>;
};

function SheetField({
  label,
  hint,
  value,
  onChange,
  onSubmit,
  type = "text",
  inputMode,
  autoComplete,
  inputRef,
}: SheetFieldProps) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--ink-2)",
          letterSpacing: 0.2,
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <input
        ref={inputRef}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onSubmit) {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder={hint}
        style={{
          width: "100%",
          height: 48,
          padding: "0 14px",
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          fontSize: 16,
          color: "var(--ink-1)",
          outline: "none",
          fontFamily: "inherit",
        }}
      />
    </label>
  );
}

function formatError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: string | Array<{ msg?: string }> } | string | null;
    if (typeof body === "string" && body.length > 0) return body;
    if (body && typeof body === "object" && "detail" in body) {
      const d = (body as { detail?: unknown }).detail;
      if (typeof d === "string") return d;
      if (Array.isArray(d) && d.length > 0) {
        const first = d[0] as { msg?: string };
        if (first?.msg) return first.msg;
      }
    }
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
